/**
 * UI Store - Pinia store for UI-related state and functionality
 * Manages theme, drawer, notifications, loading states, etc.
 */

import { defineStore } from 'pinia'
import { setThemeMode as applyThemeMode } from '@/services/themeService'
import type {
  UIState,
  ThemeMode,
  ThemeModeWithSystem,
  Notification,
  NotificationOptions,
  SetLoadingPayload,
  ThemePreference
} from './types/ui.types'
import type { SearchScope } from '@/types/search'

/**
 * Initialize theme from localStorage preference
 */
function getInitialTheme(): ThemeMode {
  try {
    const raw = localStorage.getItem('lyt_theme_preference')
    if (raw) {
      const { mode } = JSON.parse(raw) as ThemePreference
      if (mode === 'dark') return 'dark'
      if (mode === 'light') return 'light'
      if (mode === 'system') {
        const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
        return prefersDark ? 'dark' : 'light'
      }
    }
  } catch (error) {
    console.error('Failed to load theme preference:', error)
  }
  return 'light'
}

export const useUIStore = defineStore('ui', {
  state: (): UIState => ({
    theme: getInitialTheme(),
    drawer: false,
    searchQuery: '',
    searchScope: (localStorage.getItem('lyt_search_scope') as SearchScope) || 'all',
    showCart: false,
    notifications: [],
    isLoading: false,
    loadingMessage: ''
  }),

  getters: {
    /**
     * Get current theme (resolved to light/dark)
     */
    currentTheme(state): ThemeMode {
      return state.theme
    },

    /**
     * Get the raw theme preference (including 'system')
     */
    themePreference(): ThemeModeWithSystem {
      try {
        const stored = localStorage.getItem('lyt_theme_preference')
        if (stored === 'light' || stored === 'dark' || stored === 'system') {
          return stored
        }
      } catch {
        // Ignore localStorage errors
      }
      return this.theme
    },

    /**
     * Check if drawer is open
     */
    isDrawerOpen(state): boolean {
      return state.drawer
    },

    /**
     * Get search query
     */
    searchQueryValue(state): string {
      return state.searchQuery
    },

    /**
     * Get current search scope
     */
    currentSearchScope(state): SearchScope {
      return state.searchScope
    },

    /**
     * Check if cart is visible
     */
    isCartVisible(state): boolean {
      return state.showCart
    },

    /**
     * Get all notifications
     */
    allNotifications(state): Notification[] {
      return state.notifications
    },

    /**
     * Check if app is loading
     */
    isAppLoading(state): boolean {
      return state.isLoading
    },

    /**
     * Get loading message
     */
    currentLoadingMessage(state): string {
      return state.loadingMessage
    }
  },

  actions: {
    /**
     * Set theme (only 'light' or 'dark')
     * For 'system' mode, use setThemeMode instead
     */
    setTheme(theme: ThemeMode) {
      const safeTheme = theme === 'dark' ? 'dark' : 'light'
      this.theme = safeTheme
      // Delegate persistence and <html data-theme> updates to themeService
      applyThemeMode(safeTheme)
    },

    /**
     * Toggle between light and dark theme
     */
    toggleTheme(): ThemeMode {
      const newTheme: ThemeMode = this.theme === 'light' ? 'dark' : 'light'
      this.setTheme(newTheme)
      return newTheme
    },

    /**
     * Set theme mode (including 'system' option)
     */
    setThemeMode(mode: ThemeModeWithSystem) {
      if (!['light', 'dark', 'system'].includes(mode)) {
        console.warn(`Invalid theme mode: ${mode}`)
        return
      }

      applyThemeMode(mode)

      // Update local UI theme to a concrete value for Vuetify
      if (mode === 'system') {
        const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
        this.theme = prefersDark ? 'dark' : 'light'
      } else {
        this.theme = mode
      }
    },

    /**
     * Set drawer open/closed state
     */
    setDrawer(value: boolean) {
      this.drawer = value
    },

    /**
     * Toggle drawer open/closed
     */
    toggleDrawer() {
      this.drawer = !this.drawer
    },

    /**
     * Set search query
     */
    setSearchQuery(query: string) {
      this.searchQuery = query
    },

    /**
     * Clear search query
     */
    clearSearchQuery() {
      this.searchQuery = ''
    },

    /**
     * Set search scope and persist to localStorage
     */
    setSearchScope(scope: SearchScope) {
      this.searchScope = scope
      localStorage.setItem('lyt_search_scope', scope)
    },

    /**
     * Set cart visibility
     */
    setShowCart(value: boolean) {
      this.showCart = value
    },

    /**
     * Toggle cart visibility
     */
    toggleCart() {
      this.showCart = !this.showCart
    },

    /**
     * Add a notification to the notifications list
     * Automatically removes after duration (default 5s) if autoClose is not false
     */
    addNotification(notification: Omit<Notification, 'id' | 'timestamp'>): number {
      const id = Date.now()
      this.notifications.push({
        id,
        ...notification,
        timestamp: new Date()
      })

      // Automatically remove the notification after delay if autoClose is not false
      if (notification.autoClose !== false) {
        setTimeout(() => {
          this.removeNotification(id)
        }, notification.duration || 5000)
      }

      return id
    },

    /**
     * Remove a notification by ID
     */
    removeNotification(id: number) {
      const index = this.notifications.findIndex(n => n.id === id)
      if (index > -1) {
        this.notifications.splice(index, 1)
      }
    },

    /**
     * Clear all notifications
     */
    clearNotifications() {
      this.notifications = []
    },

    /**
     * Set loading state
     * Accepts either a boolean or an object with isLoading and message
     */
    setLoading(payload: boolean | SetLoadingPayload) {
      if (typeof payload === 'object') {
        this.isLoading = payload.isLoading
        this.loadingMessage = payload.message || ''
      } else {
        // Backward compatibility
        this.isLoading = payload
        this.loadingMessage = ''
      }
    },

    /**
     * Show success notification
     */
    showSuccess(message: string, options: NotificationOptions = {}): number {
      return this.addNotification({
        type: 'success',
        message,
        ...options
      })
    },

    /**
     * Show error notification
     */
    showError(message: string, options: NotificationOptions = {}): number {
      return this.addNotification({
        type: 'error',
        message,
        ...options
      })
    },

    /**
     * Show info notification
     */
    showInfo(message: string, options: NotificationOptions = {}): number {
      return this.addNotification({
        type: 'info',
        message,
        ...options
      })
    },

    /**
     * Show warning notification
     */
    showWarning(message: string, options: NotificationOptions = {}): number {
      return this.addNotification({
        type: 'warning',
        message,
        ...options
      })
    }
  }
})
